import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createBlock as _createBlock, withCtx as _withCtx, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "invoicing-display-settings" }
const _hoisted_2 = { class: "invoicing-display-settings__columns" }
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = { class: "invoicing-display-settings__staff-labels" }
const _hoisted_5 = { class: "invoicing-display-settings__actions" }
const _hoisted_6 = { class: "invoicing-display-settings__description typographies-paragraph-medium-regular" }
const _hoisted_7 = { class: "invoicing-display-settings__actions" }
const _hoisted_8 = ["innerHTML"]
const _hoisted_9 = { class: "typographies-paragraph-small-regular" }
const _hoisted_10 = { class: "invoicing-display-settings__actions" }
const _hoisted_11 = { key: 1 }
const _hoisted_12 = { class: "invoicing-display-settings__actions" }

import { ref, inject, provide, onMounted, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { Translator, PushToast } from 'js/shims-vue';
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import InvoicingDisplayFieldsTable from 'vcomponents/settings/invoicing/InvoicingDisplayFieldsTable.vue';
import TranslationText from 'vcomponents/common/TranslationText.vue';
import { useTranslationLinkParser } from 'composables/useTranslationLinkParser';
import {
    getMetadata,
    getDisplayPreferences,
    updateDisplayPreferences,
    type InvoicingSettingsMetadata,
    type DisplayPreferences,
    type ManualFooter,
    type FooterTranslation,
    FooterMode,
} from 'api/settings/invoicing/invoicingDisplaySettingsService';
import type { AssignedStaffLabel } from 'api/settings/assignedStaffLabelsService';
import { useLanguagesStore } from 'js/vue/stores/languages/languagesStore';
import RichText from 'vcomponents/richtext/RichText.vue';
import { replaceTagsWithSpan, replaceSpansWithTags } from 'Helpers/richTextHelper.js';
import { captureException } from '@sentry/vue';
import Feature from 'Services/Feature';

// DisplayPreferences has assigned_staff_label_id (number) and footer (object), so keyof DisplayPreferences makes the value type too wide.
// narrow the parameter type to only accept boolean fields
type BooleanDisplayPref = {
    [K in keyof DisplayPreferences]: DisplayPreferences[K] extends boolean ? K : never;
}[keyof DisplayPreferences];

interface FieldEntry {
    field: BooleanDisplayPref;
    label: string;
    value: boolean;
    isForced: boolean;
}

type SaveSection = 'companyInfo' | 'clientInfo' | 'productsPayments' | 'footer';

const SEMIBOLD_CLASS = 'typographies-paragraph-medium-semibold';


export default /*@__PURE__*/_defineComponent({
  __name: 'InvoicingDisplaySettings',
  setup(__props) {

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const store = useStore();
const languagesStore = useLanguagesStore();

const isLoading = ref(true);
const savingSection = ref<SaveSection | null>(null);
const isSaving = computed((): boolean => savingSection.value !== null);
const metadata = ref<InvoicingSettingsMetadata | null>(null);
const displayPreferences = ref<DisplayPreferences | null>(null);
const currentTab = ref('default');

const optionsRichText = ['Bold', 'Divider', 'CustomTagsAndFields'];

const providedTags = computed((): { category: string; name: string; key: string }[] =>
    (metadata.value?.custom_tags ?? []).map((tag) => ({
        category: tag.category,
        name: tag.description,
        key: tag.name,
    })),
);
provide('customTagsAndFields', providedTags);

const alertAction = {
    label: $t.__('common.actions.view_faq'),
    callback: () =>
        window.open(
            'https://help.sellsy.com/fr/articles/5871533-modifier-le-pied-de-page-de-mes-documents',
            '_blank',
        ),
};

const { parseTranslation } = useTranslationLinkParser();

const translations: Record<string, string> = {
    companyInfoTitle: $t.__('settings.invoicingDisplay.companyInfo.title'),
    clientInfoDescription: $t.__('settings.invoicingDisplay.clientInfo.description'),
    clientInfoTitle: $t.__('settings.invoicingDisplay.clientInfo.title'),
    productsPaymentsInfoTitle: $t.__('settings.invoicingDisplay.productsPaymentsInfo.title'),
    productsPaymentsInfoDescription: $t.__(
        'settings.invoicingDisplay.productsPaymentsInfo.description',
        { boldStart: '<span class="typographies-paragraph-medium-semibold">', boldEnd: '</span>' },
    ),
    productColumnsHeader: $t.__('settings.invoicingDisplay.productColumns.header'),
    quantityPrecisionLabel: $t.__('settings.invoicingDisplay.quantityPrecision.label'),
    paymentInfoHeader: $t.__('settings.invoicingDisplay.paymentInfo.header'),
    generalInfoHeader: $t.__('settings.invoicingDisplay.generalInfo.header'),
    signaturesLabel: $t.__('settings.invoicingDisplay.signatures.label'),
    footerTitle: $t.__('settings.invoicingDisplay.footer.title'),
    footerAuto: $t.__('settings.invoicingDisplay.footer.auto'),
    footerManual: $t.__('settings.invoicingDisplay.footer.manual'),
    footerAutoCaption: $t.__('settings.invoicingDisplay.footer.autoCaption'),
    footerManualWarning: $t.__('settings.invoicingDisplay.footer.manualWarning'),
    tableInformation: $t.__('settings.invoicingDisplay.table.information'),
    tableClientInformation: $t.__('settings.invoicingDisplay.table.clientInformation'),
    staffLabelLabel: $t.__('settings.invoicingDisplay.staffLabel.label'),
    staffLabelInfo: $t.__('settings.invoicingDisplay.staffLabel.info'),
    staffLabelConfigureButton: $t.__('settings.invoicingDisplay.staffLabel.configureButton'),
};

const quantityPrecisionDescription = parseTranslation(
    $t.__('settings.invoicingDisplay.quantityPrecision.description'),
);

const companyInfoDescription = $t.__('settings.invoicingDisplay.companyInfo.description', {
    startBold: `<span class="${SEMIBOLD_CLASS}">`,
    endBold: '</span>',
});

const buildFieldEntries = (fields: BooleanDisplayPref[]): FieldEntry[] => {
    return fields.map((field) => {
        return {
            field,
            label: $t.__(`settings.invoicingDisplay.field.${field}`),
            value: displayPreferences.value?.[field] ?? false,
            isForced:
                (metadata.value?.enforce_display_rules &&
                    metadata.value.forced_prefs.includes(field)) ??
                false,
        };
    });
};

const companyInfoKeys: BooleanDisplayPref[] = [
    'show_assigned_staff_identity',
    'show_assigned_staff_email',
    'show_assigned_staff_phone',
    'show_assigned_staff_mobile',
    'show_assigned_staff_fax',
    'show_address',
];

const clientInfoKeys: BooleanDisplayPref[] = [
    'show_contact_name',
    'show_company_phone_number',
    'show_vat_number',
    'show_siret',
    'show_siren',
    'show_company_reference',
];

const productInfoKeys: BooleanDisplayPref[] = [
    'show_reference_column',
    'show_notes_column',
    'show_unit_cost_column',
    'show_taxes_column',
    'show_quantity_column',
    'show_amount_column',
    'show_image_column',
    'show_discount_column',
    'show_barcode_values',
    'show_barcode_images',
];

const paymentInfoKeys: BooleanDisplayPref[] = [
    'show_payment_terms',
    'show_payment_deadlines',
    'show_payment_methods',
    'show_deposit_details',
    'show_bank_account',
    'show_check_label',
];

const generalInfoKeys: BooleanDisplayPref[] = [
    'show_tax_basis',
    'show_vat_mention',
    'show_parent_document',
    'show_payment_date',
    'show_total',
    'show_tax_details',
    'show_item_count',
    'show_item_weight',
    'show_prices_on_delivery',
];

const signatureKeys: BooleanDisplayPref[] = [
    'show_signature_on_estimate',
    'show_signature_on_order',
    'show_signature_on_delivery',
];

const getDecimalLabel = (count: number): string => {
    return $t.n__('settings.invoicingDisplay.quantityPrecision.decimal', null, count, count);
};

const showSignatures = ref<typeof signatureKeys>([]);

const signatures = signatureKeys.map((key) => ({
    key,
    label: $t.__(`settings.invoicingDisplay.field.${key}`),
}));

const companyInfoFields = computed((): FieldEntry[] => buildFieldEntries(companyInfoKeys));

const clientInfoFields = computed((): FieldEntry[] => buildFieldEntries(clientInfoKeys));

const visibleProductInfoKeys = computed((): BooleanDisplayPref[] => {
    return productInfoKeys.filter(
        (key) => !(key === 'show_notes_column' && displayPreferences.value?.show_notes_column),
    );
});

const productInfoFields = computed((): FieldEntry[] =>
    buildFieldEntries(visibleProductInfoKeys.value),
);

const paymentInfoFields = computed((): FieldEntry[] => buildFieldEntries(paymentInfoKeys));

const visibleGeneralInfoKeys = computed((): BooleanDisplayPref[] => {
    return generalInfoKeys.filter(
        (key) =>
            !(key === 'show_vat_mention' && Feature.isEnabled('force-vat-mention')) &&
            !(key === 'show_tax_basis' && Feature.isEnabled('force-vat-base-display')),
    );
});

const generalInfoFields = computed((): FieldEntry[] =>
    buildFieldEntries(visibleGeneralInfoKeys.value),
);

const isManualFooter = computed((): boolean => {
    return displayPreferences.value?.footer.mode === FooterMode.MANUAL;
});

const manualFooter = computed((): ManualFooter => {
    return displayPreferences.value?.footer as ManualFooter;
});

const defaultTranslation: FooterTranslation = { lang_id: 0, value: '' };

const getFooterTranslation = (langId: number): FooterTranslation => {
    const footer = manualFooter.value;
    if (!footer) {
        return defaultTranslation;
    }
    if (footer.value === undefined) {
        footer.value = '';
    }
    if (!footer.translations) {
        footer.translations = [];
    }
    let translation = footer.translations.find((entry) => entry.lang_id === langId);
    if (!translation) {
        translation = { lang_id: langId, value: '' };
        footer.translations.push(translation);
    }
    return translation;
};

const activeStaffLabels = computed((): AssignedStaffLabel[] | undefined => {
    return metadata.value?.assigned_staff_labels.filter((item) => item.is_active);
});

const fetchInitialData = async (): Promise<void> => {
    isLoading.value = true;
    try {
        const [metadataResponse, preferencesResponse] = await Promise.all([
            getMetadata(),
            getDisplayPreferences(),
        ]);
        metadata.value = metadataResponse.data;
        displayPreferences.value = {
            ...preferencesResponse.data,
            quantity_precision: preferencesResponse.data.quantity_precision ?? -1,
        };
        if (displayPreferences.value.footer.mode === FooterMode.MANUAL) {
            const tags = providedTags.value;
            displayPreferences.value.footer.value = replaceTagsWithSpan(
                displayPreferences.value.footer.value,
                tags,
            );
            displayPreferences.value.footer.translations =
                displayPreferences.value.footer.translations.map((t) => ({
                    ...t,
                    value: replaceTagsWithSpan(t.value, tags),
                }));
        }
        showSignatures.value = signatureKeys.filter(
            (key) => preferencesResponse.data[key as keyof DisplayPreferences],
        );
        languagesStore.languages = metadataResponse.data.languages;
    } catch {
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            text: $t.__('common.labels.toast.error'),
        });
    } finally {
        isLoading.value = false;
    }
};

const updateValue = (newValue: boolean, field: keyof DisplayPreferences): void => {
    if (displayPreferences.value) {
        (displayPreferences.value[field] as boolean) = newValue;
    }
};

const openStaffLabelsDialog = (): void => {
    store.dispatch('popupPanel/open', {
        key: 'assignedStaffLabelsModal',
        props: {
            callback: fetchInitialData,
        },
    });
};

const pickBooleanFields = (fields: BooleanDisplayPref[]): Partial<DisplayPreferences> => {
    if (!displayPreferences.value) {
        return {};
    }
    const picked = {} as Record<BooleanDisplayPref, boolean>;
    for (const field of fields) {
        picked[field] = displayPreferences.value[field];
    }
    return picked;
};

const buildFooterPayload = (): Partial<DisplayPreferences> => {
    if (!displayPreferences.value) {
        return {};
    }
    const footerPref = displayPreferences.value.footer;
    if (footerPref.mode !== FooterMode.MANUAL) {
        return { footer: { mode: FooterMode.AUTO } };
    }
    const tags = providedTags.value;
    return {
        footer: {
            mode: FooterMode.MANUAL,
            value: replaceSpansWithTags(footerPref.value, tags) ?? '',
            translations: (footerPref.translations ?? [])
                .filter((entry) => Boolean(entry.value))
                .map((entry) => ({
                    ...entry,
                    value: replaceSpansWithTags(entry.value, tags) ?? '',
                })),
        },
    };
};

const buildSectionPayload = (section: SaveSection): Partial<DisplayPreferences> => {
    if (!displayPreferences.value) {
        return {};
    }
    switch (section) {
        case 'companyInfo':
            return {
                ...pickBooleanFields(companyInfoKeys),
                assigned_staff_label_id: displayPreferences.value.assigned_staff_label_id,
            };
        case 'clientInfo':
            return pickBooleanFields(clientInfoKeys);
        case 'productsPayments':
            return {
                ...pickBooleanFields(visibleProductInfoKeys.value),
                ...pickBooleanFields(paymentInfoKeys),
                ...pickBooleanFields(visibleGeneralInfoKeys.value),
                ...pickBooleanFields(signatureKeys),
                quantity_precision:
                    displayPreferences.value.quantity_precision === -1
                        ? null
                        : displayPreferences.value.quantity_precision,
            };
        case 'footer':
            return buildFooterPayload();
    }
};

const handleSave = async (section: SaveSection): Promise<void> => {
    if (!displayPreferences.value) {
        return;
    }
    savingSection.value = section;
    try {
        await updateDisplayPreferences(buildSectionPayload(section));
        $pushToast({
            type: CompassEnums.ToastType.SUCCESS,
            text: $t.__('settings.invoicingDisplay.toast.saveSuccess'),
        });
    } catch (error) {
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            text: $t.__('settings.invoicingDisplay.toast.saveError'),
        });
        captureException(error);
    } finally {
        savingSection.value = null;
    }
};

const getLanguageIcon = (flag: string): string => {
    return `/ressources/public/img/flags/${flag ?? 'blank'}.png`;
};

watch(showSignatures, (checkedKeys) => {
    if (!displayPreferences.value) {
        return;
    }
    for (const key of signatureKeys) {
        (displayPreferences.value[key as keyof DisplayPreferences] as boolean) =
            checkedKeys.includes(key);
    }
});

onMounted(() => {
    fetchInitialData();
});

return (_ctx: any,_cache: any) => {
  const _component_CompassDropdownOption = _resolveComponent("CompassDropdownOption")!
  const _component_CompassSelect = _resolveComponent("CompassSelect")!
  const _component_CompassButton = _resolveComponent("CompassButton")!
  const _component_CompassFormItem = _resolveComponent("CompassFormItem")!
  const _component_CompassCheckbox = _resolveComponent("CompassCheckbox")!
  const _component_CompassCheckboxGroup = _resolveComponent("CompassCheckboxGroup")!
  const _component_CompassRadio = _resolveComponent("CompassRadio")!
  const _component_CompassRadioGroup = _resolveComponent("CompassRadioGroup")!
  const _component_CompassAlert = _resolveComponent("CompassAlert")!
  const _component_CompassTabPane = _resolveComponent("CompassTabPane")!
  const _component_CompassTabs = _resolveComponent("CompassTabs")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(Card, null, {
        default: _withCtx(() => [
          _createVNode(Heading, {
            labelTitle: translations.companyInfoTitle,
            variant: "h2",
            prefixColoredIcon: { icon: 'company', color: 'brand' }
          }, null, 8 /* PROPS */, ["labelTitle"]),
          _createElementVNode("p", {
            class: "invoicing-display-settings__description typographies-paragraph-medium-regular",
            innerHTML: _unref(companyInfoDescription)
          }, null, 8 /* PROPS */, _hoisted_3),
          _createVNode(InvoicingDisplayFieldsTable, {
            headerLabel: translations.tableInformation,
            fields: companyInfoFields.value,
            loading: isLoading.value,
            onChange: updateValue
          }, null, 8 /* PROPS */, ["headerLabel", "fields", "loading"]),
          (displayPreferences.value && !isLoading.value)
            ? (_openBlock(), _createBlock(_component_CompassFormItem, {
                key: 0,
                class: "invoicing-display-settings__staff-form-item",
                label: translations.staffLabelLabel,
                info: translations.staffLabelInfo
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_4, [
                    _createVNode(_component_CompassSelect, {
                      modelValue: displayPreferences.value.assigned_staff_label_id,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((displayPreferences.value.assigned_staff_label_id) = $event))
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeStaffLabels.value, (item) => {
                          return (_openBlock(), _createBlock(_component_CompassDropdownOption, {
                            key: item.id,
                            label: item.label,
                            value: item.id
                          }, null, 8 /* PROPS */, ["label", "value"]))
                        }), 128 /* KEYED_FRAGMENT */))
                      ]),
                      _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["modelValue"]),
                    _createVNode(_component_CompassButton, {
                      label: translations.staffLabelConfigureButton,
                      type: _ctx.CompassEnums.ButtonType.TERTIARY,
                      leftIcon: "settings-fill",
                      onClick: openStaffLabelsDialog
                    }, null, 8 /* PROPS */, ["label", "type"])
                  ])
                ]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["label", "info"]))
            : _createCommentVNode("v-if", true),
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_component_CompassButton, {
              label: _unref($t).__('common.actions.save'),
              disabled: isLoading.value || isSaving.value,
              loading: savingSection.value === 'companyInfo',
              onClick: _cache[1] || (_cache[1] = ($event: any) => (handleSave('companyInfo')))
            }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
          ])
        ]),
        _: 1 /* STABLE */
      }),
      _createVNode(Card, null, {
        default: _withCtx(() => [
          _createVNode(Heading, {
            labelTitle: translations.clientInfoTitle,
            variant: "h2",
            prefixColoredIcon: { icon: 'user-circle', color: 'brand' }
          }, null, 8 /* PROPS */, ["labelTitle"]),
          _createElementVNode("p", _hoisted_6, _toDisplayString(translations.clientInfoDescription), 1 /* TEXT */),
          _createVNode(InvoicingDisplayFieldsTable, {
            headerLabel: translations.tableClientInformation,
            fields: clientInfoFields.value,
            loading: isLoading.value,
            onChange: updateValue
          }, null, 8 /* PROPS */, ["headerLabel", "fields", "loading"]),
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_component_CompassButton, {
              label: _unref($t).__('common.actions.save'),
              disabled: isLoading.value || isSaving.value,
              loading: savingSection.value === 'clientInfo',
              onClick: _cache[2] || (_cache[2] = ($event: any) => (handleSave('clientInfo')))
            }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
          ])
        ]),
        _: 1 /* STABLE */
      })
    ]),
    _createVNode(Card, null, {
      default: _withCtx(() => [
        _createVNode(Heading, {
          labelTitle: translations.productsPaymentsInfoTitle,
          variant: "h2",
          prefixColoredIcon: { icon: 'list', color: 'brand' }
        }, null, 8 /* PROPS */, ["labelTitle"]),
        _createElementVNode("p", {
          class: "invoicing-display-settings__description typographies-paragraph-medium-regular",
          innerHTML: translations.productsPaymentsInfoDescription
        }, null, 8 /* PROPS */, _hoisted_8),
        _createVNode(InvoicingDisplayFieldsTable, {
          class: "invoicing-display-settings__table--margin",
          headerLabel: translations.productColumnsHeader,
          fields: productInfoFields.value,
          loading: isLoading.value,
          onChange: updateValue
        }, null, 8 /* PROPS */, ["headerLabel", "fields", "loading"]),
        (displayPreferences.value && !isLoading.value)
          ? (_openBlock(), _createBlock(_component_CompassFormItem, {
              key: 0,
              class: "invoicing-display-settings__number-form-item",
              label: translations.quantityPrecisionLabel
            }, {
              default: _withCtx(() => [
                _createVNode(_component_CompassSelect, {
                  modelValue: displayPreferences.value.quantity_precision,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((displayPreferences.value.quantity_precision) = $event))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_CompassDropdownOption, {
                      label: _unref($t).__('common.labels.default_value'),
                      value: -1
                    }, null, 8 /* PROPS */, ["label"]),
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(10, (n) => {
                      return _createVNode(_component_CompassDropdownOption, {
                        key: n - 1,
                        label: getDecimalLabel(n - 1),
                        value: n - 1
                      }, null, 8 /* PROPS */, ["label", "value"])
                    }), 64 /* STABLE_FRAGMENT */))
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue"]),
                _createElementVNode("p", _hoisted_9, [
                  _createVNode(TranslationText, {
                    parts: _unref(quantityPrecisionDescription),
                    linkSize: _ctx.CompassEnums.LinkSize.SMALL
                  }, null, 8 /* PROPS */, ["parts", "linkSize"])
                ])
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["label"]))
          : _createCommentVNode("v-if", true),
        _createVNode(InvoicingDisplayFieldsTable, {
          class: "invoicing-display-settings__table--margin-large",
          headerLabel: translations.paymentInfoHeader,
          fields: paymentInfoFields.value,
          loading: isLoading.value,
          onChange: updateValue
        }, null, 8 /* PROPS */, ["headerLabel", "fields", "loading"]),
        _createVNode(InvoicingDisplayFieldsTable, {
          class: "invoicing-display-settings__table--margin",
          headerLabel: translations.generalInfoHeader,
          fields: generalInfoFields.value,
          loading: isLoading.value,
          onChange: updateValue
        }, null, 8 /* PROPS */, ["headerLabel", "fields", "loading"]),
        (!isLoading.value)
          ? (_openBlock(), _createBlock(_component_CompassFormItem, {
              key: 1,
              label: translations.signaturesLabel
            }, {
              default: _withCtx(() => [
                _createVNode(_component_CompassCheckboxGroup, {
                  modelValue: showSignatures.value,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((showSignatures).value = $event)),
                  direction: _ctx.CompassEnums.ChoicesListPosition.HORIZONTAL
                }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(signatures), (signature) => {
                      return (_openBlock(), _createBlock(_component_CompassCheckbox, {
                        key: signature.key,
                        modelValue: signature.key,
                        label: signature.label
                      }, null, 8 /* PROPS */, ["modelValue", "label"]))
                    }), 128 /* KEYED_FRAGMENT */))
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue", "direction"])
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["label"]))
          : _createCommentVNode("v-if", true),
        _createElementVNode("div", _hoisted_10, [
          _createVNode(_component_CompassButton, {
            label: _unref($t).__('common.actions.save'),
            disabled: isLoading.value || isSaving.value,
            loading: savingSection.value === 'productsPayments',
            onClick: _cache[5] || (_cache[5] = ($event: any) => (handleSave('productsPayments')))
          }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
        ])
      ]),
      _: 1 /* STABLE */
    }),
    _createVNode(Card, null, {
      default: _withCtx(() => [
        _createVNode(Heading, {
          labelTitle: translations.footerTitle,
          variant: "h2",
          prefixColoredIcon: { icon: 'signature', color: 'brand' }
        }, null, 8 /* PROPS */, ["labelTitle"]),
        (!isLoading.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              (displayPreferences.value)
                ? (_openBlock(), _createBlock(_component_CompassRadioGroup, {
                    key: 0,
                    modelValue: displayPreferences.value.footer.mode,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((displayPreferences.value.footer.mode) = $event))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_CompassRadio, {
                        modelValue: _unref(FooterMode).AUTO,
                        label: translations.footerAuto,
                        caption: translations.footerAutoCaption
                      }, null, 8 /* PROPS */, ["modelValue", "label", "caption"]),
                      _createVNode(_component_CompassRadio, {
                        modelValue: _unref(FooterMode).MANUAL,
                        label: translations.footerManual
                      }, null, 8 /* PROPS */, ["modelValue", "label"])
                    ]),
                    _: 1 /* STABLE */
                  }, 8 /* PROPS */, ["modelValue"]))
                : _createCommentVNode("v-if", true),
              (isManualFooter.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                    _createVNode(_component_CompassAlert, {
                      class: "invoicing-display-settings__footer-alert",
                      type: _ctx.CompassEnums.AlertType.WARNING,
                      size: _ctx.CompassEnums.AlertSize.SMALL,
                      description: translations.footerManualWarning,
                      showClose: false,
                      action: alertAction
                    }, null, 8 /* PROPS */, ["type", "size", "description"]),
                    (displayPreferences.value)
                      ? (_openBlock(), _createBlock(_component_CompassTabs, {
                          key: _unref(languagesStore).languages.length,
                          modelValue: currentTab.value,
                          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((currentTab).value = $event))
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_CompassTabPane, {
                              label: _unref($t).__('common.labels.by_default'),
                              name: "default",
                              icon: "flag-fill"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(RichText, {
                                  modelValue: manualFooter.value.value,
                                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((manualFooter.value.value) = $event)),
                                  options: optionsRichText,
                                  class: "custom-richtext"
                                }, null, 8 /* PROPS */, ["modelValue"])
                              ]),
                              _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["label"]),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(languagesStore).languages, (language) => {
                              return (_openBlock(), _createBlock(_component_CompassTabPane, {
                                key: language.id,
                                label: language.lang,
                                name: language.lang,
                                customIcon: getLanguageIcon(language.flag)
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(RichText, {
                                    modelValue: getFooterTranslation(language.id).value,
                                    "onUpdate:modelValue": ($event: any) => ((getFooterTranslation(language.id).value) = $event),
                                    options: optionsRichText,
                                    class: "custom-richtext"
                                  }, null, 8 /* PROPS */, ["modelValue", "onUpdate:modelValue"])
                                ]),
                                _: 2 /* DYNAMIC */
                              }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["label", "name", "customIcon"]))
                            }), 128 /* KEYED_FRAGMENT */))
                          ]),
                          _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["modelValue"]))
                      : _createCommentVNode("v-if", true)
                  ]))
                : _createCommentVNode("v-if", true)
            ], 64 /* STABLE_FRAGMENT */))
          : _createCommentVNode("v-if", true),
        _createElementVNode("div", _hoisted_12, [
          _createVNode(_component_CompassButton, {
            label: _unref($t).__('common.actions.save'),
            disabled: isLoading.value || isSaving.value,
            loading: savingSection.value === 'footer',
            onClick: _cache[9] || (_cache[9] = ($event: any) => (handleSave('footer')))
          }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
        ])
      ]),
      _: 1 /* STABLE */
    })
  ]))
}
}

})